import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

interface User {
    email: string;
    name: string;
    picture?: string;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (token: string, user?: User) => void;
    logout: () => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(localStorage.getItem('admin_token'));
    const initialUser = localStorage.getItem('admin_user');
    const [user, setUser] = useState<User | null>(initialUser ? JSON.parse(initialUser) : null);

    useEffect(() => {
        if (token && !user) {
            try {
                // If it looks like a JWT, try to decode it
                if (token.includes('.') && token.split('.').length === 3) {
                    const decoded: any = jwtDecode(token);
                    const userData = {
                        email: decoded.email,
                        name: decoded.name,
                        picture: decoded.picture
                    };
                    setUser(userData);
                    localStorage.setItem('admin_user', JSON.stringify(userData));
                } else {
                    // It's likely an access token. We expect userData to be passed via login()
                    // or already present in localStorage (initialUser).
                    // If neither, we might need to rely on the next login or another fetch.
                    console.log('Token is not a JWT, skipping decode.');
                }
            } catch (error) {
                console.error('Failed to decode token:', error);
                // Don't auto-logout for non-JWT tokens as they might be valid access tokens
                if (token.includes('.')) {
                    logout();
                }
            }
        }
    }, [token, user]);

    const login = (newToken: string, userData?: User) => {
        localStorage.setItem('admin_token', newToken);
        setToken(newToken);
        if (userData) {
            localStorage.setItem('admin_user', JSON.stringify(userData));
            setUser(userData);
        }
    };

    const logout = () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
